import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, rupee } from "../lib/api";
import { useStores } from "../lib/stores";
import { ArrowLeft, ScanLine, Trash2 } from "lucide-react";
import { toast } from "sonner";

const ROW_GRID =
  "grid w-full grid-cols-[minmax(0,1.4fr)_72px_96px_80px_40px] items-center gap-x-3 px-4";

export default function PurchaseForm() {
  const navigate = useNavigate();
  const { activeStoreId } = useStores();
  const [barcode, setBarcode] = useState("");
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [supplierPhone, setSupplierPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();

  useEffect(() => {
    inputRef.current?.focus();
    api.get("/suppliers").then((r) => setSuppliers(r.data));
  }, []);

  useEffect(() => {
    if (!supplierId) return;
    const s = suppliers.find((x) => x.id === supplierId);
    if (s) {
      setSupplierName(s.name);
      setSupplierPhone(s.phone || "");
    }
  }, [supplierId, suppliers]);

  const scan = async (e) => {
    e.preventDefault();
    if (!barcode.trim()) return;
    try {
      const { data: p } = await api.get(`/products/barcode/${barcode.trim()}`);
      addItem(p);
      setBarcode("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Barcode not found");
    }
  };

  const addItem = (p) => {
    setItems((prev) => {
      const found = prev.find((x) => x.product_id === p.id);
      if (found) {
        return prev.map((x) =>
          x.product_id === p.id ? { ...x, quantity: x.quantity + 1 } : x
        );
      }
      return [
        ...prev,
        {
          product_id: p.id,
          product_name: p.name,
          barcode: p.barcode,
          quantity: 1,
          unit_cost: p.cost_price || 0,
          gst_percent: p.gst_percent ?? 5,
          stock: p.stock,
        },
      ];
    });
  };

  const setQty = (id, qty) => {
    const n = Math.max(1, parseInt(qty, 10) || 1);
    setItems((prev) => prev.map((x) => (x.product_id === id ? { ...x, quantity: n } : x)));
  };

  const setCost = (id, cost) => {
    const n = Math.max(0, parseFloat(cost) || 0);
    setItems((prev) => prev.map((x) => (x.product_id === id ? { ...x, unit_cost: n } : x)));
  };

  const remove = (id) => setItems((prev) => prev.filter((x) => x.product_id !== id));

  const lineTotal = (it) => {
    const sub = it.unit_cost * it.quantity;
    const tax = sub * ((it.gst_percent || 0) / 100);
    return sub + tax;
  };

  const totals = items.reduce(
    (acc, it) => {
      const sub = it.unit_cost * it.quantity;
      const tax = sub * ((it.gst_percent || 0) / 100);
      acc.subtotal += sub;
      acc.tax += tax;
      acc.total += sub + tax;
      return acc;
    },
    { subtotal: 0, tax: 0, total: 0 }
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!supplierName.trim()) return toast.error("Supplier name is required");
    if (items.length === 0) return toast.error("Add at least one product");

    setBusy(true);
    try {
      const payload = {
        store_id: activeStoreId || undefined,
        notes: notes || undefined,
        items: items.map((it) => ({
          product_id: it.product_id,
          quantity: it.quantity,
          unit_cost: it.unit_cost,
          gst_percent: it.gst_percent,
        })),
      };
      if (supplierId) {
        payload.supplier_id = supplierId;
      } else {
        payload.supplier_name = supplierName.trim();
        payload.supplier_phone = supplierPhone || undefined;
      }

      const { data } = await api.post("/purchases", payload);
      toast.success(`Purchase ${data.purchase_no} recorded — stock updated`);
      navigate(`/app/purchases/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save purchase");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto" data-testid="purchase-form-root">
      <Link to="/app/purchases" className="inline-flex items-center gap-2 text-sm font-semibold mb-6" style={{ color: "var(--primary)" }}>
        <ArrowLeft size={16} /> Purchases
      </Link>

      <h1 className="font-display font-bold text-3xl">New stock purchase</h1>
      <p className="text-sm mt-1 mb-8" style={{ color: "var(--text-muted)" }}>
        Scan barcodes to add items · stock increases on save
      </p>

      <form onSubmit={submit} className="space-y-6">
        <div className="card-soft p-5 grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Saved supplier</label>
            <select
              value={supplierId}
              onChange={(e) => setSupplierId(e.target.value)}
              className="mt-1.5 w-full h-11 px-3 rounded-md border bg-white text-sm"
              style={{ borderColor: "var(--border-soft)" }}
            >
              <option value="">— New / one-time supplier —</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>{s.name}{s.phone ? ` · ${s.phone}` : ""}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium">Supplier name <span style={{ color: "var(--primary)" }}>*</span></label>
            <input
              required
              value={supplierName}
              onChange={(e) => setSupplierName(e.target.value)}
              disabled={!!supplierId}
              className="mt-1.5 w-full h-11 px-3 rounded-md border bg-white disabled:opacity-70"
              style={{ borderColor: "var(--border-soft)" }}
              placeholder="Wholesaler / manufacturer"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Supplier phone</label>
            <input
              value={supplierPhone}
              onChange={(e) => setSupplierPhone(e.target.value)}
              disabled={!!supplierId}
              className="mt-1.5 w-full h-11 px-3 rounded-md border bg-white disabled:opacity-70"
              style={{ borderColor: "var(--border-soft)" }}
            />
          </div>
          <div>
            <label className="text-sm font-medium">Notes</label>
            <input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1.5 w-full h-11 px-3 rounded-md border bg-white"
              style={{ borderColor: "var(--border-soft)" }}
              placeholder="Bill no., delivery ref…"
            />
          </div>
        </div>

        <div className="card-soft overflow-hidden">
          <form onSubmit={scan} className="p-4 border-b flex gap-2" style={{ borderColor: "var(--border-soft)" }}>
            <div className="relative flex-1">
              <ScanLine size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
              <input
                ref={inputRef}
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                placeholder="Scan or type barcode"
                className="w-full h-12 pl-10 pr-3 rounded-md border bg-white font-mono"
                style={{ borderColor: "var(--border-soft)" }}
              />
            </div>
            <button type="submit" className="h-12 px-5 rounded-md text-white font-semibold tactile-btn" style={{ background: "var(--primary)" }}>
              Add
            </button>
          </form>

          {items.length === 0 ? (
            <div className="py-12 text-center text-sm" style={{ color: "var(--text-muted)" }}>
              Scan products to add purchase lines
            </div>
          ) : (
            <>
              <div className={`${ROW_GRID} py-2 text-xs font-semibold uppercase tracking-wide border-b`} style={{ borderColor: "var(--border-soft)", color: "var(--text-muted)" }}>
                <div>Product</div>
                <div>Qty</div>
                <div>Unit cost</div>
                <div className="text-right">Line total</div>
                <div />
              </div>
              {items.map((it) => (
                <div key={it.product_id} className={`${ROW_GRID} py-3 border-b`} style={{ borderColor: "var(--border-soft)" }}>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{it.product_name}</div>
                    <div className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>{it.barcode}</div>
                  </div>
                  <input
                    type="number"
                    min={1}
                    value={it.quantity}
                    onChange={(e) => setQty(it.product_id, e.target.value)}
                    className="h-10 px-2 rounded-md border text-center num"
                    style={{ borderColor: "var(--border-soft)" }}
                  />
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={it.unit_cost}
                    onChange={(e) => setCost(it.product_id, e.target.value)}
                    className="h-10 px-2 rounded-md border num"
                    style={{ borderColor: "var(--border-soft)" }}
                  />
                  <div className="text-right text-sm font-semibold num">{rupee(lineTotal(it))}</div>
                  <button type="button" onClick={() => remove(it.product_id)} className="w-9 h-9 grid place-items-center rounded-md hover:bg-red-50" style={{ color: "var(--error)" }}>
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </>
          )}
        </div>

        {items.length > 0 && (
          <div className="card-soft p-5 flex flex-wrap items-end justify-between gap-4">
            <div className="text-sm space-y-1" style={{ color: "var(--text-muted)" }}>
              <div>Subtotal <span className="num font-medium text-[var(--text-main)]">{rupee(totals.subtotal)}</span></div>
              <div>GST <span className="num font-medium text-[var(--text-main)]">{rupee(totals.tax)}</span></div>
              <div className="text-lg font-display font-bold" style={{ color: "var(--primary)" }}>
                Total {rupee(totals.total)}
              </div>
            </div>
            <button
              type="submit"
              disabled={busy}
              className="h-12 px-8 rounded-md text-white font-semibold tactile-btn"
              style={{ background: "var(--primary)" }}
            >
              {busy ? "Saving…" : "Save purchase"}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
